import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Popup, Polygon, useMap } from 'react-leaflet';

// Fix default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

function svgMarker(color, letter = '') {
  const html = `
<div style="position:relative;display:flex;align-items:center;justify-content:center;">
  <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 40C16 40 30 22 30 14C30 6.26801 23.732 0 16 0C8.26801 0 2 6.26801 2 14C2 22 16 40 16 40Z" fill="${color}" stroke="white" stroke-width="2"/>
    <circle cx="16" cy="14" r="6" fill="white"/>
  </svg>
  <div style="position:absolute;top:6px;color:${color};font-weight:700;font-size:12px;font-family:'DM Sans',sans-serif;">${letter}</div>
</div>`;
  return L.divIcon({
    className: 'custom-marker',
    html,
    iconSize: [32, 40],
    iconAnchor: [16, 40],
    popupAnchor: [0, -36],
  });
}

export const RESTAURANT_ICON = svgMarker('#1F6F5B', 'R');
export const CUSTOMER_ICON = svgMarker('#2E7BCF', 'C');
// Bigger rider marker with a pulsing halo — highly visible on any map background.
function riderMarker(label = 'RIDER') {
  const color = '#FF7A59';
  const html = `
<div style="position:relative;display:flex;flex-direction:column;align-items:center;">
  <div style="position:absolute;top:14px;width:50px;height:50px;border-radius:50%;background:${color};opacity:.25;animation:momaRiderPulse 1.6s ease-out infinite;"></div>
  <svg width="46" height="58" viewBox="0 0 32 40" style="filter:drop-shadow(0 3px 8px rgba(0,0,0,.35));" xmlns="http://www.w3.org/2000/svg">
    <path d="M16 40C16 40 30 22 30 14C30 6.268 23.732 0 16 0C8.268 0 2 6.268 2 14C2 22 16 40 16 40Z" fill="${color}" stroke="white" stroke-width="2.5"/>
    <circle cx="16" cy="14" r="7" fill="white"/>
    <text x="16" y="17.5" text-anchor="middle" font-family="DM Sans,sans-serif" font-weight="800" font-size="10" fill="${color}">🏍</text>
  </svg>
  <div style="margin-top:-6px;background:${color};color:white;font-weight:700;font-size:10px;padding:2px 8px;border-radius:9999px;border:2px solid white;white-space:nowrap;box-shadow:0 2px 4px rgba(0,0,0,.2);">${label}</div>
</div>
<style>@keyframes momaRiderPulse{0%{transform:scale(.7);opacity:.55}70%{transform:scale(1.6);opacity:0}100%{opacity:0}}</style>`;
  return L.divIcon({ className: 'custom-marker rider-marker', html, iconSize: [46, 72], iconAnchor: [23, 58], popupAnchor: [0, -54] });
}
export const RIDER_ICON = riderMarker('RIDER');
export const buildRiderIcon = (name) => riderMarker(name || 'RIDER');

// Component to recenter map when position changes
export function Recenter({ position }) {
  const map = useMap();
  const lat = position?.[0];
  const lng = position?.[1];
  useEffect(() => {
    if (lat != null && lng != null) {
      map.setView([lat, lng], map.getZoom(), { animate: true });
    }
  }, [lat, lng, map]);
  return null;
}

export function FitBounds({ bounds }) {
  const map = useMap();
  const boundsKey = React.useMemo(
    () => (Array.isArray(bounds) ? JSON.stringify(bounds) : ''),
    [bounds]
  );
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
    }
    // boundsKey is a stable serialization of bounds; map is stable per instance
  }, [boundsKey, bounds, map]);
  return null;
}

export const BaseMap = ({ children, center = [45.4642, 9.19], zoom = 13, className = '', style = {} }) => (
  <div className={`rounded-2xl overflow-hidden border border-[--brand-border] shadow-[var(--shadow-sm)] ${className}`} style={style}>
    <MapContainer center={center} zoom={zoom} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true}>
      <TileLayer
        attribution='&copy; <a href="https://openstreetmap.org">OSM</a>'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      {children}
    </MapContainer>
  </div>
);

export { L, Marker, Popup, Polygon };
